export interface SalesUnit {
  id: string            // unique slug within the product (e.g. 'bundle', 'loose', 'print')
  name: string          // display name (e.g. 'Bundle', 'Loose', 'A4 Printing')
  conversionFactor: number  // how many base units = 1 of this sales unit (Bundle=500, Loose=1)
  price: number
  wholesalePrice?: number | null
  discountPrice?: number | null
  isDefault?: boolean
}

export interface PriceTier {
  name: string       // e.g. 'retail', 'wholesale', 'marked', 'special'
  price: number
  minQuantity?: number  // minimum qty for this tier to apply
}

/** Volume pricing: "every N units at price P", remainder at base price. Customer-facing only. */
export interface VolumePriceRule {
  blockSize: number    // e.g. 500 (min 1)
  pricePerUnit: number // e.g. 2.5 (>= 0)
}

export interface VolumePricing {
  rules: VolumePriceRule[]  // applied largest blockSize first
}

export interface Product {
  id: string
  _id?: string
  name: string
  description?: string
  sku: string
  barcode?: string
  isbn?: string
  price: number              // Default selling price (retail)
  cost: number               // Cost price - purchase price from supplier
  wholesalePrice?: number    // Wholesale price - for bulk selling
  discountPrice?: number     // Discount/promotional price - active promotion price
  markedPrice?: number       // Marked price - item issued price from the company/manufacturer
  priceTiers?: PriceTier[]   // Additional custom price tiers
  volumePricing?: VolumePricing  // Customer volume/block pricing (e.g. every 500 @ Rs.2.5)
  category: ProductCategory
  supplier?: Supplier
  supplierId?: string
  stock: number
  minStock: number
  maxStock: number
  reorderLevel?: number  // Per-product reorder level
  reorderQty?: number    // How many to reorder
  expiryDate?: string    // ISO date string for expiry tracking
  unit: string
  baseUnit?: string          // Smallest trackable unit (e.g. 'sheet', 'mug', 'piece')
  salesUnits?: SalesUnit[]   // Multiple selling units with conversion factors
  variantGroupId?: string    // Shared ID linking products in the same variant group (e.g. A4 Paper colors)
  variantGroupName?: string  // Display name for the variant group (e.g. "A4 Paper")
  // Runtime-only fields set by posStore when expanding salesUnits (not in backend)
  _masterProductId?: string  // Real product ID for batch/stock lookups
  _salesUnit?: SalesUnit     // Active sales unit for this virtual card
  image?: string
  isActive: boolean
  tags: string[]
  createdAt: Date
  updatedAt: Date
}

export interface ProductCategory {
  id: string
  _id?: string
  name: string
  slug?: string
  icon?: string
  description?: string
  parentId?: string
  order?: number
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface Supplier {
  id: string
  _id?: string
  name: string
  contactPerson?: string
  company?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  notes?: string
  isActive: boolean
  totalOrders: number
  totalSpent: number
  createdAt: Date | string
  updatedAt: Date | string
}

export interface GRNItem {
  productId?: string
  productName: string
  sku?: string
  barcode?: string
  quantity: number
  unitCost: number
  sellingPrice?: number
  wholesalePrice?: number
  lineTotal: number
  expiryDate?: string
  notes?: string
  receivingUnitId?: string    // Sales unit used for receiving (e.g. 'bundle')
  receivingUnitFactor?: number // Conversion factor at time of receiving
}

export interface GRN {
  id: string
  _id?: string
  grnNumber: string
  supplierId?: string
  supplierName: string
  items: GRNItem[]
  totalItems: number
  totalCost: number
  invoiceNumber?: string
  invoiceDate?: string
  paymentStatus: 'pending' | 'partial' | 'paid'
  notes?: string
  receivedBy: string
  receivedByName?: string
  status: string
  createdAt: Date | string
  updatedAt?: Date | string
}

export interface Expense {
  id: string
  _id?: string
  title: string
  amount: number
  category: string
  date: string
  paymentMethod: string
  reference?: string
  notes?: string
  createdBy: string
  createdByName?: string
  status: string
  createdAt: Date | string
  updatedAt?: Date | string
}

export const EXPENSE_CATEGORIES = [
  'Rent', 'Utilities', 'Salaries', 'Supplies', 'Transport', 'Marketing',
  'Maintenance', 'Insurance', 'Taxes', 'Internet', 'Phone', 'Food & Beverages',
  'Office Supplies', 'Equipment', 'Miscellaneous', 'General'
]

export type ChequeStatus = 'pending' | 'deposited' | 'cleared' | 'bounced' | 'cancelled' | 'expired'
export type ChequeType = 'issued' | 'received'

export interface Cheque {
  id: string
  _id?: string
  chequeNumber: string
  type: ChequeType
  bankName: string
  payee: string
  amount: number
  issueDate: string
  depositDate?: string | null
  clearDate?: string | null
  status: ChequeStatus
  notes?: string | null
  createdBy: string
  createdByName?: string
  createdAt: Date | string
  updatedAt?: Date | string
}

export const CHEQUE_STATUS_CONFIG: Record<ChequeStatus, { label: string; color: string; bgColor: string }> = {
  pending:   { label: 'Pending',   color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  deposited: { label: 'Deposited', color: 'text-blue-400',   bgColor: 'bg-blue-500/20' },
  cleared:   { label: 'Cleared',   color: 'text-green-400',  bgColor: 'bg-green-500/20' },
  bounced:   { label: 'Returned / Bounced', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  cancelled: { label: 'Cancelled', color: 'text-gray-400',   bgColor: 'bg-gray-500/20' },
  expired:   { label: 'Expired',   color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
}

export const CHEQUE_STATUS_TRANSITIONS: Record<ChequeStatus, ChequeStatus[]> = {
  pending: ['deposited', 'cancelled', 'expired'],
  deposited: ['cleared', 'bounced'],
  bounced: ['pending', 'cancelled'],
  cleared: [],
  cancelled: [],
  expired: [],
}

export const SRI_LANKAN_BANKS = [
  'Bank of Ceylon', 'People\'s Bank', 'Commercial Bank', 'Hatton National Bank',
  'Sampath Bank', 'Seylan Bank', 'NDB Bank', 'DFCC Bank', 'Nations Trust Bank',
  'Pan Asia Banking Corp', 'Union Bank', 'Amana Bank', 'HSBC', 'Standard Chartered',
  'Cargills Bank', 'Other'
]

export interface StockBatch {
  id: string
  _id?: string
  productId: string
  batchNumber: string
  grnId?: string
  supplierId?: string
  supplierName?: string
  costPrice: number
  sellingPrice: number
  wholesalePrice?: number | null
  discountPrice?: number | null
  markedPrice?: number | null
  receivedQuantity: number
  currentStock: number
  receivedDate: string
  expiryDate?: string | null
  notes?: string | null
  isActive: boolean
  createdAt: string | Date
  updatedAt: string | Date
}

export type PriceTierKey = 'retail' | 'wholesale' | 'marked' | 'cost' | string

export interface CartItem {
  product: Product
  quantity: number
  discount?: number
  notes?: string
  selectedPriceTier?: PriceTierKey   // Which price tier is active (default: 'retail')
  unitPriceOverride?: number          // The actual price being used (for display/calculation)
  batchId?: string                    // Stock batch this item was sold from
  batchLabel?: string                 // Human-readable batch label for display
  salesUnitId?: string               // Sales unit selected (e.g. 'bundle')
  salesUnitName?: string             // Display name (e.g. 'Bundle')
  salesUnitFactor?: number           // Conversion factor used (e.g. 500)
  isReturn?: boolean                    // True if this is a return credit line item
  originalQuantity?: number             // For return items: the max qty that can be returned
  returnFromTransactionId?: string      // Original transaction ID the item is being returned from
  returnFromReceiptNumber?: string      // Original receipt number for display (e.g. "RCP-1738...")
}

export type PaymentMethod = 'cash' | 'card' | 'digital_wallet' | 'bank_transfer' | 'payhere' | 'split' | 'wallet'

export interface PaymentSplit {
  method: Exclude<PaymentMethod, 'split'>
  amount: number
  reference?: string
}

export type TransactionType = 'sale' | 'bill_payment'

export interface Transaction {
  id: string
  idempotencyKey?: string   // stable per-sale key; dedupes retries/offline-queue replays on the server
  type?: TransactionType
  transactionNumber?: string
  receiptNumber?: string
  /** For bill_payment, billType/provider/accountRef/reference */
  billType?: string
  billProvider?: string
  billAccountRef?: string
  billReference?: string
  items: CartItem[] | Array<{
    productId?: string
    productName: string
    sku: string
    quantity: number
    unitPrice: number
    discount: number
    tax: number
    subtotal: number
    total: number
    notes?: string
  }>
  customerId?: string
  customerName?: string
  employeeId: string
  paymentMethod: PaymentMethod
  paymentSplits?: PaymentSplit[]
  discount: number
  tax: number
  subtotal: number
  total: number
  amountPaid?: number
  /** Amount already paid as advance (deducted from total at checkout). */
  advanceApplied?: number
  change?: number
  status: TransactionStatus
  notes?: string
  createdAt: Date | string
  updatedAt?: Date | string
}

export type TransactionStatus = 'pending' | 'completed' | 'cancelled' | 'refunded' | 'partial_refund' | 'voided' | 'held'

export interface HeldTransaction {
  id: string
  name: string
  cart: CartItem[]
  customerId?: string
  customerName?: string
  discount: number
  discountType: 'percentage' | 'fixed'
  tax: number
  paymentMethod: PaymentMethod
  paymentSplits?: PaymentSplit[]
  notes?: string
  heldAt: Date
  heldBy: string
}

export interface RefundItem {
  productId: string
  productName: string
  quantity: number
  refundAmount: number
  reason?: string
  batchId?: string
  batchLabel?: string
}

export interface Refund {
  id: string
  refundNumber: string
  originalTransactionId: string
  originalTransactionNumber?: string
  items: RefundItem[]
  subtotal: number
  tax: number
  total: number
  refundMethod: Exclude<PaymentMethod, 'split'>
  reason: string
  processedBy: string
  status: 'pending' | 'completed' | 'rejected'
  createdAt: Date
}

export interface Customer {
  id: string
  _id?: string
  name?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  address?: Address
  dateOfBirth?: Date
  loyaltyPoints: number
  totalSpent: number
  totalOrders?: number
  lastPurchaseAt?: Date
  lastOrderDate?: string
  isActive: boolean
  notes?: string
  customerType?: 'wholesale' | 'regular' | 'special'
  creditLimit?: number   // max debt allowed for 'special' customers (wallet can go to -creditLimit)
  walletBalance?: number
  createdAt: Date
  updatedAt: Date
}

export interface Address {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
}

export interface QuickButton {
  id: string
  productId: string
  productName: string
  position: number
}

export interface Receipt {
  id: string
  transactionId: string
  receiptNumber: string
  customer?: Customer
  items: CartItem[]
  subtotal: number
  discount: number
  tax: number
  total: number
  paymentMethod: PaymentMethod
  paymentSplits?: PaymentSplit[]
  amountPaid?: number
  change?: number
  createdAt: Date
  printedAt?: Date
  emailedAt?: Date
}

export interface POSSettings {
  currency: string
  currencySymbol: string
  taxRate: number
  defaultPaymentMethod: PaymentMethod
  receiptFooter?: string
  receiptHeader?: string
  storeName?: string
  storeAddress?: string
  storePhone?: string
  barcodeScannerEnabled: boolean
  cashDrawerEnabled: boolean
  receiptPrinterEnabled: boolean
  emailReceiptEnabled: boolean
  lowStockAlertEnabled: boolean
  lowStockThreshold: number
  soundEnabled: boolean
}

export interface StockAlert {
  productId: string
  productName: string
  currentStock: number
  minStock: number
  severity: 'warning' | 'critical'
}

// Quick amount presets for cash payments
export interface QuickAmount {
  value: number
  label: string
}

export const DEFAULT_QUICK_AMOUNTS: QuickAmount[] = [
  { value: 50, label: 'Rs.50' },
  { value: 100, label: 'Rs.100' },
  { value: 500, label: 'Rs.500' },
  { value: 1000, label: 'Rs.1000' },
  { value: 5000, label: 'Rs.5000' },
]

export const DEFAULT_POS_SETTINGS: POSSettings = {
  currency: 'LKR',
  currencySymbol: 'Rs.',
  taxRate: 8.0,
  defaultPaymentMethod: 'cash',
  receiptFooter: 'Thank you for your purchase!',
  receiptHeader: 'Sachin Poth Madura',
  barcodeScannerEnabled: true,
  cashDrawerEnabled: false,
  receiptPrinterEnabled: true,
  emailReceiptEnabled: true,
  lowStockAlertEnabled: true,
  lowStockThreshold: 5,
  soundEnabled: true,
}
